import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { referralsApi, AffiliateApplicationData } from '../api/referrals';
import { useAuthStore } from '../store/authStore';

const COUNTRIES = [
  'Spain', 'Lithuania', 'Germany', 'France', 'Italy', 'Portugal',
  'Netherlands', 'Belgium', 'Poland', 'United Kingdom', 'Ireland',
  'Austria', 'Switzerland', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Czech Republic', 'Romania', 'Hungary', 'Greece', 'Bulgaria',
  'United States', 'Canada', 'Mexico', 'Brazil', 'Argentina',
  'Australia', 'New Zealand', 'Japan', 'South Korea', 'Other',
];

export default function AffiliateApplyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState<AffiliateApplicationData>({
    fullName: '',
    country: '',
    companyName: '',
    website: '',
    socialMedia: '',
    howWillPromote: '',
    payoutMethod: 'bank',
    iban: '',
    paypalEmail: '',
    taxId: '',
  });

  // Check if user already has an application
  const { data: applicationStatus, isLoading: loadingStatus } = useQuery({
    queryKey: ['affiliateApplicationStatus'],
    queryFn: referralsApi.getApplicationStatus,
    enabled: isAuthenticated,
  });

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/affiliate/apply');
    }
  }, [isAuthenticated, navigate]);

  // Redirect to dashboard if already approved
  useEffect(() => {
    if (applicationStatus?.status === 'approved') {
      navigate('/affiliate/dashboard');
    }
  }, [applicationStatus, navigate]);

  const applyMutation = useMutation({
    mutationFn: referralsApi.applyForAffiliate,
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (err: any) => {
      const message = err.response?.data?.message || t('affiliate.apply.error');
      setError(message);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate payout info
    if (form.payoutMethod === 'bank' && !form.iban) {
      setError(t('affiliate.apply.error_iban_required'));
      return;
    }
    if (form.payoutMethod === 'paypal' && !form.paypalEmail) {
      setError(t('affiliate.apply.error_paypal_required'));
      return;
    }

    applyMutation.mutate(form);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Show pending status
  if (applicationStatus?.status === 'pending') {
    return (
      <div className="py-12 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-2xl p-8">
            <div className="text-5xl mb-4">⏳</div>
            <h1 className="text-2xl font-bold text-white mb-4">
              {t('affiliate.status.pending', 'Application Pending')}
            </h1>
            <p className="text-gray-300">
              {t('affiliate.status.pending_description', 'Your application is being reviewed. We\'ll notify you by email within 48 hours.')}
            </p>
            <Link
              to="/home"
              className="inline-block mt-6 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
            >
              {t('common.backToHome', 'Back to Home')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show rejected status with option to re-apply
  if (applicationStatus?.status === 'rejected') {
    return (
      <div className="py-12 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-8">
            <div className="text-5xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-white mb-4">
              {t('affiliate.status.rejected', 'Application Rejected')}
            </h1>
            {applicationStatus.rejectionReason && (
              <p className="text-gray-300 mb-4">
                {t('affiliate.status.rejection_reason', 'Reason')}: {applicationStatus.rejectionReason}
              </p>
            )}
            <p className="text-gray-400 mb-6">
              {t('affiliate.status.can_reapply', 'You can submit a new application below.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (success) {
    return (
      <div className="py-12 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="bg-green-500/20 border border-green-500/50 rounded-2xl p-8">
            <div className="text-5xl mb-4">🎉</div>
            <h1 className="text-2xl font-bold text-white mb-4">
              {t('affiliate.apply.success_title', 'Application Submitted!')}
            </h1>
            <p className="text-gray-300">
              {t('affiliate.apply.success_description', 'We\'ll review your application and get back to you within 48 hours.')}
            </p>
            <Link
              to="/home"
              className="inline-block mt-6 px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-semibold rounded-xl hover:from-primary-500 hover:to-secondary-500 transition-all"
            >
              {t('common.backToHome', 'Back to Home')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('affiliate.apply.title', 'Become a Fonorex Affiliate')}
          </h1>
          <p className="text-gray-400">
            {t('affiliate.apply.description', 'Fill out this form to apply for our affiliate program. We\'ll review your application within 48 hours.')}
          </p>
        </div>

        {/* Form */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 rounded-xl">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('affiliate.apply.fullName', 'Full Name / Company Name')} *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('affiliate.apply.country', 'Country')} *
                </label>
                <select
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                >
                  <option value="">{t('affiliate.apply.select_country', 'Select country')}</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('affiliate.apply.companyName', 'Company Name')}
                  <span className="text-gray-500 font-normal ml-1">({t('common.optional', 'optional')})</span>
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('affiliate.apply.website', 'Website')}
                  <span className="text-gray-500 font-normal ml-1">({t('common.optional', 'optional')})</span>
                </label>
                <input
                  type="url"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  placeholder="https://"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t('affiliate.apply.socialMedia', 'Social Media Profile')}
                <span className="text-gray-500 font-normal ml-1">({t('common.optional', 'optional')})</span>
              </label>
              <input
                type="text"
                name="socialMedia"
                value={form.socialMedia}
                onChange={handleChange}
                placeholder="Instagram, LinkedIn, Twitter..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t('affiliate.apply.howWillPromote', 'How do you plan to promote Fonorex?')}
              </label>
              <textarea
                name="howWillPromote"
                value={form.howWillPromote}
                onChange={handleChange}
                rows={3}
                placeholder={t('affiliate.apply.howWillPromote_placeholder', 'Tell us about your audience and promotion strategy...')}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
              />
            </div>

            {/* Payout Info */}
            <div className="border-t border-white/10 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                {t('affiliate.apply.payoutInfo', 'Payout Information')}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('affiliate.apply.payoutMethod', 'Payout Method')} *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payoutMethod"
                      value="bank"
                      checked={form.payoutMethod === 'bank'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-white">{t('affiliate.apply.bankTransfer', 'Bank Transfer (IBAN)')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payoutMethod"
                      value="paypal"
                      checked={form.payoutMethod === 'paypal'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-white">PayPal</span>
                  </label>
                </div>
              </div>

              {form.payoutMethod === 'bank' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    IBAN *
                  </label>
                  <input
                    type="text"
                    name="iban"
                    value={form.iban}
                    onChange={handleChange}
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none uppercase"
                  />
                </div>
              )}

              {form.payoutMethod === 'paypal' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    PayPal Email *
                  </label>
                  <input
                    type="email"
                    name="paypalEmail"
                    value={form.paypalEmail}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  />
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('affiliate.apply.taxId', 'Tax ID / VAT Number')}
                  <span className="text-gray-500 font-normal ml-1">({t('common.optional', 'optional')})</span>
                </label>
                <input
                  type="text"
                  name="taxId"
                  value={form.taxId}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={applyMutation.isPending}
              className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-glow"
            >
              {applyMutation.isPending
                ? t('common.submitting', 'Submitting...')
                : t('affiliate.apply.submit', 'Submit Application')}
            </button>

            <p className="text-center text-sm text-gray-500">
              {t('affiliate.apply.terms', 'By submitting, you agree to our affiliate program terms.')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
